import { FC } from 'react';
import { HiArrowRight, HiPlus, HiShieldCheck } from 'react-icons/hi';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib';
import RouteConstants from '@/features/router/RouteConstants';
import type { EmailCampaignGate } from '../hooks/useEmailCampaignGate';

type EmailCampaignPrerequisiteBannerProps = {
  variant: Exclude<EmailCampaignGate, 'ready'>;
};

const getBannerMessageKey = (variant: Exclude<EmailCampaignGate, 'ready'>) => {
  if (variant === 'connect-mailtrap') {
    return 'emailWorkspace.campaigns.createDisabledBannerConnect';
  }

  if (variant === 'no-audience') {
    return 'emailWorkspace.gates.noAudienceDescription';
  }

  if (variant === 'domain-not-verified') {
    return 'emailWorkspace.campaigns.createDisabledBannerDomainNotVerified';
  }

  return 'emailWorkspace.campaigns.createDisabledBannerDomain';
};

export const EmailCampaignPrerequisiteBanner: FC<EmailCampaignPrerequisiteBannerProps> = ({ variant }) => {
  const { t } = useTranslation('campaign');
  const history = useHistory();

  const handlePrimaryAction = () => {
    if (variant === 'connect-mailtrap') {
      history.push(RouteConstants.destinations);
      return;
    }

    if (variant === 'no-audience') {
      history.push(RouteConstants.audiencesCreate);
      return;
    }

    if (variant === 'domain-not-verified') {
      history.push(RouteConstants.emailDomains);
      return;
    }

    history.push(RouteConstants.emailDomains, { openAddForm: true });
  };

  const isConnectVariant = variant === 'connect-mailtrap';
  const isNoAudienceVariant = variant === 'no-audience';

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
        isConnectVariant ? 'border-orange-200 bg-orange-50' : isNoAudienceVariant ? 'border-violet-200 bg-violet-50' : 'border-blue-200 bg-blue-50'
      }`}
    >
      <p className={`text-sm ${isConnectVariant ? 'text-orange-800' : isNoAudienceVariant ? 'text-violet-800' : 'text-blue-800'}`}>
        {t(getBannerMessageKey(variant))}
      </p>

      <Button intent='secondary' size='sm' className='inline-flex shrink-0 items-center gap-2' onClick={handlePrimaryAction}>
        {variant === 'connect-mailtrap' ? (
          <>
            {t('emailWorkspace.gates.goToDestinations')}
            <HiArrowRight className='size-4' />
          </>
        ) : isNoAudienceVariant ? (
          <>
            <HiPlus className='size-4' />
            {t('emailWorkspace.gates.noAudienceAction')}
          </>
        ) : variant === 'domain-not-verified' ? (
          <>
            <HiShieldCheck className='size-4' />
            {t('emailWorkspace.gates.verifySendingDomain')}
          </>
        ) : (
          <>
            <HiPlus className='size-4' />
            {t('emailWorkspace.gates.addSendingDomain')}
          </>
        )}
      </Button>
    </div>
  );
};
